import React from 'react';
import { X, MapPin, AlertTriangle, Calendar, CloudSun, Send, ShieldAlert, BadgeCheck, CreditCard, ChevronRight, Star, Fuel, ShoppingBag, TrendingUp, TrendingDown, Minus, Camera, Zap, Search, Droplet, UserMinus, BusFront, Sparkles, Eye } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { toast } from 'sonner';
import { NIGERIAN_STATES, LGAS_BY_STATE } from '../constants/locations';
import { VoiceReporter } from './VoiceReporter';

interface CreatePostModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (post: any) => void;
  user: { name: string } | null;
}

const RESTRICTED_KEYWORDS = [
  'president', 'governor', 'vice president', 'vp', 'minister', 'senator',
  'convoy', 'motorcade', 'official movement', 'state house', 'aso rock',
  'buhari', 'tinubu', 'shettima', 'politician', 'government official',
  'private house', 'residence', 'apartment', 'home address', 'individual', 
  'tracking', 'following', 'stalking', 'private citizen', 'personally',
  'phone number', 'house number', 'street number'
];

type PostType = 'traffic' | 'event' | 'weather' | 'fuel' | 'market' | 'help' | 'power' | 'find' | 'nuisance' | 'water' | 'bus' | 'activity' | 'crime';
type HelpType = 'general' | 'directions' | 'medical' | 'police' | 'fire';
type CrimeType = 'theft' | 'robbery' | 'vandalism' | 'scam' | 'other';
type PromotionTier = 'none' | 'basic' | 'venue';

const PRICING = {
  none: 0,
  basic: 20000,
  venue: 50000
};

export function CreatePostModal({ isOpen, onClose, onSubmit, user }: CreatePostModalProps) {
  React.useEffect(() => {
    console.log('🎭 CreatePostModal isOpen changed:', isOpen);
  }, [isOpen]);

  const [type, setType] = React.useState<PostType>('traffic');
  const [promoTier, setPromoTier] = React.useState<PromotionTier>('none');
  const [step, setStep] = React.useState<'details' | 'payment'>('details');
  const [isProcessing, setIsProcessing] = React.useState(false);
  const [isSubmitting, setIsSubmitting] = React.useState(false); // Add this to prevent duplicate submissions
  const [isVoiceMode, setIsVoiceMode] = React.useState(false);
  const [isScanning, setIsScanning] = React.useState(false);
  const [safetyScore, setSafetyScore] = React.useState(100);
  
  const [formData, setFormData] = React.useState({
    title: '',
    location: '',
    description: '',
    category: 'General',
    country: 'Nigeria',
    state: 'Lagos',
    lga: 'Ikeja',
    city: '',
    isAnonymous: false,
    helpType: 'general' as HelpType,
    crimeType: 'theft' as CrimeType,
    station: '',
    price: '',
    status: 'available',
    item: '',
    market: '',
    trend: 'stable',
    powerStatus: 'up',
    findStatus: 'found',
    nuisanceType: 'noise',
    severity: 'medium',
    vendorName: '',
    busStop: '',
    crowdLevel: 'medium',
    busAvailability: 'frequent',
    activityType: 'Community',
    activityTime: ''
  });

  // Draft System
  React.useEffect(() => {
    if (isOpen) {
      const savedDraft = localStorage.getItem('sentlo_post_draft');
      if (savedDraft) {
        try {
          const { type: savedType, formData: savedData } = JSON.parse(savedDraft);
          if (savedData.description || savedData.location || savedData.station) {
            toast('Draft Found', {
              description: 'You have an unsent report. Would you like to restore it?',
              action: {
                label: 'Restore',
                onClick: () => {
                  setType(savedType);
                  setFormData(savedData);
                  toast.success('Draft restored!');
                }
              },
              duration: 5000,
            });
          }
        } catch (e) {}
      }
    }
  }, [isOpen]);

  React.useEffect(() => {
    if (formData.description || formData.location || formData.station) {
      localStorage.setItem('sentlo_post_draft', JSON.stringify({ type, formData }));
    }
  }, [formData, type]);

  const clearDraft = () => {
    localStorage.removeItem('sentlo_post_draft');
  };

  const validateContent = (text: string) => {
    const lowercaseText = text.toLowerCase();
    return RESTRICTED_KEYWORDS.some(keyword => lowercaseText.includes(keyword));
  };

  const handleInitialSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Prevent duplicate submissions
    if (isSubmitting) {
      console.log('⚠️ Submission already in progress, ignoring duplicate submit');
      return;
    }

    const checkText = `${formData.location} ${formData.description} ${formData.station} ${formData.market} ${formData.item}`;
    const isRestricted = validateContent(checkText);

    if (isRestricted) {
      toast.error('Restricted Content', {
        description: 'For security and privacy reasons, sharing information about government officials or private individual activities is strictly prohibited.',
        icon: <ShieldAlert className="w-5 h-5 text-red-500" />,
        duration: 5000,
      });
      return;
    }

    if (promoTier !== 'none') {
      setStep('payment');
    } else {
      finalizePost();
    }
  };

  const finalizePost = () => {
    // Prevent duplicate submissions
    if (isSubmitting) {
      console.log('⚠️ Post submission already in progress, preventing duplicate');
      return;
    }

    setIsSubmitting(true);
    console.log('🚀 Starting post submission...');
    console.log('📝 Post type:', type);
    console.log('📝 Form data:', formData);

    let newPost: any = {
      id: Math.random().toString(36).substr(2, 9),
      ...formData,
      type,
      timestamp: 'Just now',
      reporter: formData.isAnonymous ? 'Verified Neighbor' : (user?.name || 'Anonymous'),
      isPromoted: promoTier !== 'none',
      promoTier: promoTier,
      metadata: {}
    };

    if (type === 'traffic') {
      // Ensure traffic posts have content/description field
      newPost = {
        ...newPost,
        content: formData.description || 'Traffic update',
        description: formData.description || 'Traffic update',
        location: formData.location || formData.lga || 'Unknown location'
      };
      console.log('🚗 Traffic post prepared:', newPost);
    } else if (type === 'fuel') {
      const statusText = formData.status === 'available' ? 'Available' : formData.status === 'queue' ? 'Queueing' : 'Out of Stock';
      const contentText = `${formData.station} - ${statusText}. ${formData.description || 'Fuel price update.'}`;
      newPost = {
        ...newPost,
        station: formData.station,
        location: formData.location,
        price: formData.price.startsWith('₦') ? formData.price : `₦${formData.price}`,
        status: formData.status,
        content: contentText,
        description: `${statusText} at ${formData.station}. ${formData.description || ''}`.trim(),
        metadata: {
          ...newPost.metadata,
          price: formData.price.startsWith('₦') ? formData.price : `₦${formData.price}`,
          status: formData.status,
          station: formData.station
        }
      };
      console.log('⛽ Fuel report prepared:', newPost);
    } else if (type === 'market') {
      newPost = {
        ...newPost,
        item: formData.item,
        market: formData.market,
        price: formData.price.startsWith('₦') ? formData.price : `₦${formData.price}`,
        trend: formData.trend,
        content: `${formData.item} - ${formData.trend === 'stable' ? 'Price stable' : formData.trend === 'up' ? 'Price increasing' : 'Price decreasing'}. ${formData.description || 'Market price update.'}`,
        metadata: {
          ...newPost.metadata,
          price: formData.price.startsWith('₦') ? formData.price : `₦${formData.price}`,
          item: formData.item,
          market: formData.market
        }
      };
    } else if (type === 'bus') {
      const crowdText = formData.crowdLevel === 'empty' ? 'Very quiet' : formData.crowdLevel === 'moderate' ? 'Moderate crowd' : 'Very crowded';
      const busText = formData.busAvailability === 'plenty' ? 'Buses available' : formData.busAvailability === 'few' ? 'Few buses' : 'No buses';
      newPost = {
        ...newPost,
        busStop: formData.busStop,
        crowdLevel: formData.crowdLevel,
        busAvailability: formData.busAvailability,
        title: formData.busStop,
        content: `${formData.busStop} - ${crowdText}. ${busText}. ${formData.description || ''}`.trim(),
        description: `${crowdText}. ${busText}. ${formData.description || ''}`.trim()
      };
      console.log('🚌 Bus stop report prepared:', newPost);
    } else if (type === 'activity') {
      newPost = {
        ...newPost,
        activityType: formData.activityType,
        activityTime: formData.activityTime,
        title: formData.title || 'Community Activity',
        content: `${formData.activityType} - ${formData.activityTime}. ${formData.description || ''}`.trim(),
        description: `${formData.activityType} at ${formData.activityTime}. ${formData.description || ''}`.trim()
      };
      console.log('🎉 Activity report prepared:', newPost);
    } else if (type === 'crime') {
      const severityText = formData.severity === 'high' ? 'High severity' : formData.severity === 'medium' ? 'Moderate severity' : 'Low severity';
      newPost = {
        ...newPost,
        crimeType: formData.crimeType,
        severity: formData.severity,
        title: `Security Report: ${formData.crimeType.toUpperCase()}`,
        content: `Security Alert: ${formData.crimeType} reported. ${severityText}. ${formData.description || ''}`.trim(),
        description: `${formData.crimeType} - ${severityText}. ${formData.description || ''}`.trim()
      };
      console.log('🚨 Crime report prepared:', newPost);
    } else if (type === 'event') {
      newPost.image = 'https://images.unsplash.com/photo-1769718312937-61ea18b50db1';
      newPost.attendees = 0;
    } else if (type === 'power') {
      newPost.status = formData.powerStatus;
      // Add content field for power reports
      const statusMap = {
        'up': 'Light is currently available',
        'down': 'Power outage reported',
        'restored': 'Power has been restored',
        'outage': 'Experiencing frequent power fluctuations'
      };
      newPost.content = `${statusMap[formData.powerStatus as keyof typeof statusMap] || 'Power status update'} at ${formData.location}. ${formData.description || ''}`.trim();
      newPost.metadata = {
        ...newPost.metadata,
        status: formData.powerStatus
      };
    } else if (type === 'find') {
      newPost.status = formData.findStatus;
      newPost.item = formData.item;
    } else if (type === 'nuisance') {
      newPost.type = formData.nuisanceType;
      newPost.severity = formData.severity;
    } else if (type === 'water') {
      newPost.name = formData.vendorName;
      newPost.status = formData.status;
      newPost.price = formData.price.startsWith('₦') ? formData.price : `₦${formData.price}`;
    }

    onSubmit(newPost);
    clearDraft();
    handleClose();
  };

  const handleClose = () => {
    onClose();
    setTimeout(() => {
      setStep('details');
      setPromoTier('none');
      setIsSubmitting(false); // Reset submission state
      setFormData({ 
        title: '', location: '', description: '', category: 'General', country: 'Nigeria', state: 'Lagos', lga: 'Ikeja',
        isAnonymous: false, helpType: 'general', station: '', price: '', status: 'available', 
        item: '', market: '', trend: 'stable', powerStatus: 'up', findStatus: 'found', 
        nuisanceType: 'noise', severity: 'medium', vendorName: '', busStop: '', 
        crowdLevel: 'medium', busAvailability: 'frequent', activityType: 'Community', 
        activityTime: '', city: '', crimeType: 'theft'
      });
    }, 300);
  };

  const processPayment = () => {
    setIsProcessing(true);
    setTimeout(() => {
      setIsProcessing(false);
      toast.success('Payment Successful!', {
        description: `Your ${promoTier === 'venue' ? 'Venue Spotlight' : 'Business Post'} is now live.`,
        icon: <BadgeCheck className="w-5 h-5 text-green-500" />
      });
      finalizePost();
    }, 2000);
  };

  const types = [
    { id: 'traffic', label: 'Traffic', icon: AlertTriangle, color: 'text-red-500 bg-red-50' },
    { id: 'crime', label: 'Crime', icon: ShieldAlert, color: 'text-red-700 bg-red-50' },
    { id: 'bus', label: 'Bus Stop', icon: BusFront, color: 'text-blue-600 bg-blue-50' },
    { id: 'activity', label: 'Activity', icon: Sparkles, color: 'text-purple-600 bg-purple-50' },
    { id: 'fuel', label: 'Fuel', icon: Fuel, color: 'text-orange-500 bg-orange-50' },
    { id: 'market', label: 'Market', icon: ShoppingBag, color: 'text-emerald-500 bg-emerald-50' },
    { id: 'power', label: 'Power', icon: Zap, color: 'text-yellow-600 bg-yellow-50' },
    { id: 'find', label: 'Find', icon: Search, color: 'text-blue-500 bg-blue-50' },
    { id: 'nuisance', label: 'Report', icon: ShieldAlert, color: 'text-zinc-600 bg-zinc-50' },
    { id: 'water', label: 'Water', icon: Droplet, color: 'text-cyan-500 bg-cyan-50' },
    { id: 'event', label: 'Event', icon: Calendar, color: 'text-indigo-500 bg-indigo-50' },
    { id: 'help', label: 'Ask Help', icon: UserMinus, color: 'text-amber-500 bg-amber-50' },
  ];

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleClose}
            className="absolute inset-0 bg-zinc-900/60 backdrop-blur-sm"
          />
          <motion.div
            initial={{ y: '100%', opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: '100%', opacity: 0 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="relative w-full max-w-md bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl overflow-hidden max-h-[90vh] flex flex-col"
          >
            <div className="p-6 overflow-y-auto">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold text-zinc-900">
                  {step === 'payment' ? 'Complete Payment' : isVoiceMode ? 'Voice Reporting' : 'Share Update'}
                </h2>
                <div className="flex items-center gap-2">
                  {!isVoiceMode && step === 'details' && (
                    <button 
                      onClick={() => setIsVoiceMode(true)} 
                      className="p-2 bg-blue-50 text-blue-600 rounded-xl hover:bg-blue-100 transition-colors flex items-center gap-2"
                    >
                      <Zap className="w-4 h-4 fill-current" />
                      <span className="text-[10px] font-black uppercase tracking-widest">AI Voice</span>
                    </button>
                  )}
                  <button onClick={handleClose} className="p-2 hover:bg-zinc-100 rounded-full">
                    <X className="w-5 h-5 text-zinc-500" />
                  </button>
                </div>
              </div>

              {isVoiceMode ? (
                <div className="py-4">
                  <VoiceReporter 
                    onComplete={(blob) => {
                      onSubmit({
                        type: 'traffic',
                        location: 'Current Location',
                        description: 'Audio report submitted via Sentlo AI. Transcription pending.',
                        isVoice: true,
                        timestamp: 'Just now'
                      });
                      setIsVoiceMode(false);
                      handleClose();
                    }} 
                  />
                  <button 
                    onClick={() => setIsVoiceMode(false)}
                    className="w-full mt-4 text-[10px] font-black text-zinc-400 uppercase tracking-widest py-2"
                  >
                    Switch to Text Report
                  </button>
                </div>
              ) : step === 'details' ? (
                <>
                  {/* Quick Report Buttons for common issues */}
                  <div className="mb-6 space-y-2">
                    <p className="text-[10px] font-black text-zinc-400 uppercase tracking-widest ml-1">Quick Actions (Draft Faster)</p>
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          setFormData({ ...formData, description: 'Heavy traffic at this location. Moving very slowly.', lga: 'Current Area' });
                          setType('traffic');
                          toast.success('Quick Traffic Report drafted!');
                        }}
                        className="flex items-center gap-3 p-3 bg-red-50 text-red-600 rounded-2xl border border-red-100 active:scale-95 transition-all text-left"
                      >
                        <AlertTriangle className="w-5 h-5" />
                        <span className="text-[10px] font-black uppercase leading-tight">Heavy Go-Slow Here</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setFormData({ ...formData, description: 'Power is currently out in this area.', powerStatus: 'down', lga: 'Current Area' });
                          setType('power');
                          toast.success('Quick Power Report drafted!');
                        }}
                        className="flex items-center gap-3 p-3 bg-yellow-50 text-yellow-600 rounded-2xl border border-yellow-100 active:scale-95 transition-all text-left"
                      >
                        <Zap className="w-5 h-5" />
                        <span className="text-[10px] font-black uppercase leading-tight">Light is Out Here</span>
                      </button>
                    </div>
                  </div>

                  <div className="mb-4 p-3 bg-red-50 rounded-xl flex gap-3 border border-red-100">
                    <ShieldAlert className="w-5 h-5 text-red-600 shrink-0" />
                    <div className="space-y-1">
                      <p className="text-[10px] text-red-800 leading-tight">
                        <strong>Privacy Policy:</strong> Reporting on government officials' movements or private activities is strictly prohibited.
                      </p>
                      <p className="text-[10px] text-red-800 leading-tight">
                        <strong>Integrity:</strong> Please share only accurate and appropriate information to help the community.
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-2 mb-6 overflow-x-auto pb-2 no-scrollbar">
                    {types.map((t) => (
                      <button
                        key={t.id}
                        type="button"
                        onClick={() => setType(t.id as any)}
                        className={`flex-1 min-w-[70px] flex flex-col items-center gap-2 p-3 rounded-2xl border transition-all ${
                          type === t.id
                            ? 'border-green-600 bg-green-600 text-white shadow-lg shadow-green-600/20'
                            : 'border-zinc-100 bg-zinc-50 text-zinc-500 hover:border-zinc-200'
                        }`}
                      >
                        <t.icon className={`w-5 h-5 ${type === t.id ? 'text-white' : t.color.split(' ')[0]}`} />
                        <span className="text-[10px] font-bold uppercase tracking-wider">{t.label}</span>
                      </button>
                    ))}
                  </div>

                  <form onSubmit={handleInitialSubmit} className="space-y-4">
                    {type === 'water' && (
                      <div className="mb-4 p-3 bg-cyan-50 rounded-xl flex gap-3 border border-cyan-100">
                        <Droplet className="w-5 h-5 text-cyan-600 shrink-0" />
                        <div className="space-y-1">
                          <p className="text-[10px] text-cyan-800 leading-tight">
                            <strong>Community Utility:</strong> Water vendor listings are free of charge. We do not charge vendors for these reports or for being contacted by customers.
                          </p>
                        </div>
                      </div>
                    )}

                    <div className="space-y-4">
                      <div className="space-y-1">
                        <label className="text-xs font-bold text-zinc-500 uppercase ml-1">Country</label>
                        <select
                          className="w-full bg-zinc-50 border border-zinc-200 rounded-xl py-3 px-3 text-sm focus:ring-2 focus:ring-green-600/10 focus:border-green-600 outline-hidden transition-all appearance-none"
                          value={formData.country}
                          onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                        >
                          <option value="Nigeria">Nigeria 🇳🇬</option>
                        </select>
                      </div>

                      {formData.country === 'Nigeria' ? (
                        <div className="grid grid-cols-2 gap-3">
                          <div className="space-y-1">
                            <label className="text-xs font-bold text-zinc-500 uppercase ml-1">State</label>
                            <div className="relative">
                              <select
                                className="w-full bg-zinc-50 border border-zinc-200 rounded-xl py-3 px-3 text-sm focus:ring-2 focus:ring-green-600/10 focus:border-green-600 outline-hidden transition-all appearance-none shadow-xs"
                                value={formData.state}
                                onChange={(e) => setFormData({ ...formData, state: e.target.value, lga: LGAS_BY_STATE[e.target.value]?.[0] || '' })}
                              >
                                {NIGERIAN_STATES.map(state => (
                                  <option key={state} value={state}>{state}</option>
                                ))}
                              </select>
                              <ChevronRight className="absolute right-3 top-1/2 -translate-y-1/2 w-3 h-3 text-zinc-400 rotate-90 pointer-events-none" />
                            </div>
                          </div>
                          <div className="space-y-1">
                            <label className="text-xs font-bold text-zinc-500 uppercase ml-1">LGA / Area</label>
                            <div className="relative group">
                              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-400 group-focus-within:text-green-600 transition-colors" />
                              <input
                                list="lga-suggestions"
                                type="text"
                                className="w-full bg-zinc-50 border border-zinc-200 rounded-xl py-3 pl-9 pr-4 text-sm focus:ring-2 focus:ring-green-600/10 focus:border-green-600 outline-hidden transition-all shadow-xs"
                                placeholder="Type LGA or Area..."
                                value={formData.lga}
                                onChange={(e) => setFormData({ ...formData, lga: e.target.value })}
                              />
                              <datalist id="lga-suggestions">
                                {LGAS_BY_STATE[formData.state]?.map(lga => (
                                  <option key={lga} value={lga} />
                                ))}
                              </datalist>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-1">
                          <label className="text-xs font-bold text-zinc-500 uppercase ml-1">Region / City / Area</label>
                          <input
                            required
                            type="text"
                            className="w-full bg-zinc-50 border border-zinc-200 rounded-xl py-3 px-4 text-sm focus:ring-2 focus:ring-green-600/10 focus:border-green-600 outline-hidden transition-all"
                            placeholder="e.g. Accra Central, Nairobi East, London"
                            value={formData.city}
                            onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                          />
                        </div>
                      )}
                    </div>

                    {type === 'crime' ? (
                      <>
                        <div className="grid grid-cols-2 gap-3">
                          <div className="space-y-1">
                            <label className="text-xs font-bold text-zinc-500 uppercase ml-1">Issue Type</label>
                            <select
                              className="w-full bg-zinc-50 border border-zinc-200 rounded-xl py-3 px-3 text-sm focus:ring-2 focus:ring-green-600/10 focus:border-green-600 outline-hidden transition-all appearance-none"
                              value={formData.crimeType}
                              onChange={(e) => setFormData({ ...formData, crimeType: e.target.value as any })}
                            >
                              <option value="theft">Theft / Pickpocket</option>
                              <option value="robbery">Armed Robbery</option>
                              <option value="vandalism">Vandalism</option>
                              <option value="scam">Scam / Fraud</option>
                              <option value="other">Other Incident</option>
                            </select>
                          </div>
                          <div className="space-y-1">
                            <label className="text-xs font-bold text-zinc-500 uppercase ml-1">Severity</label>
                            <select
                              className="w-full bg-zinc-50 border border-zinc-200 rounded-xl py-3 px-3 text-sm focus:ring-2 focus:ring-green-600/10 focus:border-green-600 outline-hidden transition-all appearance-none"
                              value={formData.severity}
                              onChange={(e) => setFormData({ ...formData, severity: e.target.value })}
                            >
                              <option value="low">Low (Keep Eye Out)</option>
                              <option value="medium">Medium (Stay Alert)</option>
                              <option value="high">High (Danger / Avoid)</option>
                            </select>
                          </div>
                        </div>
                        <div className="space-y-1">
                          <label className="text-xs font-bold text-zinc-500 uppercase ml-1">Location / Landmark</label>
                          <input
                            required
                            type="text"
                            className="w-full bg-zinc-50 border border-zinc-200 rounded-xl py-3 px-4 text-sm focus:ring-2 focus:ring-green-600/10 focus:border-green-600 outline-hidden transition-all"
                            placeholder="e.g. Near Oshodi Underbridge"
                            value={formData.location}
                            onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                          />
                        </div>
                      </>
                    ) : type === 'bus' ? (
                      <>
                        <div className="space-y-1">
                          <label className="text-xs font-bold text-zinc-500 uppercase ml-1">Bus Stop Name</label>
                          <input
                            required
                            type="text"
                            className="w-full bg-zinc-50 border border-zinc-200 rounded-xl py-3 px-4 text-sm focus:ring-2 focus:ring-green-600/10 focus:border-green-600 outline-hidden transition-all"
                            placeholder="e.g. Berger Bus Stop, Ojuelegba"
                            value={formData.busStop}
                            onChange={(e) => setFormData({ ...formData, busStop: e.target.value })}
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div className="space-y-1">
                            <label className="text-xs font-bold text-zinc-500 uppercase ml-1">Crowd Level</label>
                            <select
                              className="w-full bg-zinc-50 border border-zinc-200 rounded-xl py-3 px-3 text-sm focus:ring-2 focus:ring-green-600/10 focus:border-green-600 outline-hidden transition-all appearance-none"
                              value={formData.crowdLevel}
                              onChange={(e) => setFormData({ ...formData, crowdLevel: e.target.value })}
                            >
                              <option value="low">Low (Clear)</option>
                              <option value="medium">Medium</option>
                              <option value="high">High (Crowded)</option>
                              <option value="overcrowded">Overcrowded</option>
                            </select>
                          </div>
                          <div className="space-y-1">
                            <label className="text-xs font-bold text-zinc-500 uppercase ml-1">Availability</label>
                            <select
                              className="w-full bg-zinc-50 border border-zinc-200 rounded-xl py-3 px-3 text-sm focus:ring-2 focus:ring-green-600/10 focus:border-green-600 outline-hidden transition-all appearance-none"
                              value={formData.busAvailability}
                              onChange={(e) => setFormData({ ...formData, busAvailability: e.target.value })}
                            >
                              <option value="frequent">Frequent Buses</option>
                              <option value="scarce">Scarce / Slow</option>
                              <option value="none">No Bus at all</option>
                            </select>
                          </div>
                        </div>
                      </>
                    ) : type === 'activity' ? (
                      <>
                        <div className="space-y-1">
                          <label className="text-xs font-bold text-zinc-500 uppercase ml-1">What's happening?</label>
                          <input
                            required
                            type="text"
                            className="w-full bg-zinc-50 border border-zinc-200 rounded-xl py-3 px-4 text-sm focus:ring-2 focus:ring-green-600/10 focus:border-green-600 outline-hidden transition-all"
                            placeholder="e.g. Street Soccer, Community Cleaning"
                            value={formData.title}
                            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div className="space-y-1">
                            <label className="text-xs font-bold text-zinc-500 uppercase ml-1">Type</label>
                            <select
                              className="w-full bg-zinc-50 border border-zinc-200 rounded-xl py-3 px-3 text-sm focus:ring-2 focus:ring-green-600/10 focus:border-green-600 outline-hidden transition-all appearance-none"
                              value={formData.activityType}
                              onChange={(e) => setFormData({ ...formData, activityType: e.target.value })}
                            >
                              <option value="Community">Community</option>
                              <option value="Sport">Sport</option>
                              <option value="Religious">Religious</option>
                              <option value="Security">Security Patrol</option>
                              <option value="Repair">Road Repair</option>
                            </select>
                          </div>
                          <div className="space-y-1">
                            <label className="text-xs font-bold text-zinc-500 uppercase ml-1">Time</label>
                            <input
                              type="text"
                              className="w-full bg-zinc-50 border border-zinc-200 rounded-xl py-3 px-4 text-sm focus:ring-2 focus:ring-green-600/10 focus:border-green-600 outline-hidden transition-all"
                              placeholder="e.g. Happening Now, Starts 4pm"
                              value={formData.activityTime}
                              onChange={(e) => setFormData({ ...formData, activityTime: e.target.value })}
                            />
                          </div>
                        </div>
                      </>
                    ) : type === 'fuel' ? (
                      <>
                        <div className="space-y-1">
                          <label className="text-xs font-bold text-zinc-500 uppercase ml-1">Station Name</label>
                          <input
                            required
                            type="text"
                            className="w-full bg-zinc-50 border border-zinc-200 rounded-xl py-3 px-4 text-sm focus:ring-2 focus:ring-green-600/10 focus:border-green-600 outline-hidden transition-all"
                            placeholder="e.g. NNPC Retail, Total"
                            value={formData.station}
                            onChange={(e) => setFormData({ ...formData, station: e.target.value })}
                          />
                        </div>
                        <div className="space-y-1">
                          <label className="text-xs font-bold text-zinc-500 uppercase ml-1">Street / Specific Area</label>
                          <input
                            required
                            type="text"
                            className="w-full bg-zinc-50 border border-zinc-200 rounded-xl py-3 px-4 text-sm focus:ring-2 focus:ring-green-600/10 focus:border-green-600 outline-hidden transition-all"
                            placeholder="e.g. Awolowo Road, Obalende"
                            value={formData.location}
                            onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div className="space-y-1">
                            <label className="text-xs font-bold text-zinc-500 uppercase ml-1">Price per Litre</label>
                            <input
                              required
                              type="text"
                              className="w-full bg-zinc-50 border border-zinc-200 rounded-xl py-3 px-4 text-sm focus:ring-2 focus:ring-green-600/10 focus:border-green-600 outline-hidden transition-all"
                              placeholder="650"
                              value={formData.price}
                              onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                            />
                          </div>
                          <div className="space-y-1">
                            <label className="text-xs font-bold text-zinc-500 uppercase ml-1">Availability</label>
                            <select
                              className="w-full bg-zinc-50 border border-zinc-200 rounded-xl py-3 px-3 text-sm focus:ring-2 focus:ring-green-600/10 focus:border-green-600 outline-hidden transition-all appearance-none"
                              value={formData.status}
                              onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                            >
                              <option value="available">Available (No Queue)</option>
                              <option value="queue">Queueing</option>
                              <option value="exhausted">Out of Stock</option>
                            </select>
                          </div>
                        </div>
                      </>
                    ) : type === 'market' ? (
                      <>
                        <div className="space-y-1">
                          <label className="text-xs font-bold text-zinc-500 uppercase ml-1">Item Name</label>
                          <input
                            required
                            type="text"
                            className="w-full bg-zinc-50 border border-zinc-200 rounded-xl py-3 px-4 text-sm focus:ring-2 focus:ring-green-600/10 focus:border-green-600 outline-hidden transition-all"
                            placeholder="e.g. 50kg Rice"
                            value={formData.item}
                            onChange={(e) => setFormData({ ...formData, item: e.target.value })}
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div className="space-y-1">
                            <label className="text-xs font-bold text-zinc-500 uppercase ml-1">Price</label>
                            <input
                              required
                              type="text"
                              className="w-full bg-zinc-50 border border-zinc-200 rounded-xl py-3 px-4 text-sm focus:ring-2 focus:ring-green-600/10 focus:border-green-600 outline-hidden transition-all"
                              placeholder="75000"
                              value={formData.price}
                              onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                            />
                          </div>
                          <div className="space-y-1">
                            <label className="text-xs font-bold text-zinc-500 uppercase ml-1">Price Trend</label>
                            <select
                              className="w-full bg-zinc-50 border border-zinc-200 rounded-xl py-3 px-3 text-sm focus:ring-2 focus:ring-green-600/10 focus:border-green-600 outline-hidden transition-all appearance-none"
                              value={formData.trend}
                              onChange={(e) => setFormData({ ...formData, trend: e.target.value })}
                            >
                              <option value="stable">Stable</option>
                              <option value="up">Going Up</option>
                              <option value="down">Coming Down</option>
                            </select>
                          </div>
                        </div>
                      </>
                    ) : type === 'power' ? (
                      <>
                        <div className="space-y-1">
                          <label className="text-xs font-bold text-zinc-500 uppercase ml-1">Power Status</label>
                          <select
                            className="w-full bg-zinc-50 border border-zinc-200 rounded-xl py-3 px-3 text-sm focus:ring-2 focus:ring-green-600/10 focus:border-green-600 outline-hidden transition-all appearance-none"
                            value={formData.powerStatus}
                            onChange={(e) => setFormData({ ...formData, powerStatus: e.target.value })}
                          >
                            <option value="up">Light is Up (Available)</option>
                            <option value="down">Light is Out (Outage)</option>
                            <option value="restored">Just Restored</option>
                            <option value="outage">Frequent Fluctuations</option>
                          </select>
                        </div>
                        <div className="space-y-1">
                          <label className="text-xs font-bold text-zinc-500 uppercase ml-1">Specific Area / Street</label>
                          <input
                            required
                            type="text"
                            className="w-full bg-zinc-50 border border-zinc-200 rounded-xl py-3 px-4 text-sm focus:ring-2 focus:ring-green-600/10 focus:border-green-600 outline-hidden transition-all"
                            placeholder="e.g. Phase 2, Bode Thomas"
                            value={formData.location}
                            onChange={(e) => setFormData({ ...formData, location: e.target.value, title: e.target.value })}
                          />
                        </div>
                      </>
                    ) : type === 'find' ? (
                      <>
                        <div className="grid grid-cols-2 gap-3">
                          <div className="space-y-1">
                            <label className="text-xs font-bold text-zinc-500 uppercase ml-1">Status</label>
                            <select
                              className="w-full bg-zinc-50 border border-zinc-200 rounded-xl py-3 px-3 text-sm focus:ring-2 focus:ring-green-600/10 focus:border-green-600 outline-hidden transition-all appearance-none"
                              value={formData.findStatus}
                              onChange={(e) => setFormData({ ...formData, findStatus: e.target.value as any })}
                            >
                              <option value="lost">I Lost Something</option>
                              <option value="found">I Found Something</option>
                            </select>
                          </div>
                          <div className="space-y-1">
                            <label className="text-xs font-bold text-zinc-500 uppercase ml-1">Item</label>
                            <input
                              required
                              type="text"
                              className="w-full bg-zinc-50 border border-zinc-200 rounded-xl py-3 px-4 text-sm focus:ring-2 focus:ring-green-600/10 focus:border-green-600 outline-hidden transition-all"
                              placeholder="e.g. Wallet, ID Card"
                              value={formData.item}
                              onChange={(e) => setFormData({ ...formData, item: e.target.value, title: e.target.value })}
                            />
                          </div>
                        </div>
                        <div className="space-y-1">
                          <label className="text-xs font-bold text-zinc-500 uppercase ml-1">Where it happened</label>
                          <input
                            required
                            type="text"
                            className="w-full bg-zinc-50 border border-zinc-200 rounded-xl py-3 px-4 text-sm focus:ring-2 focus:ring-green-600/10 focus:border-green-600 outline-hidden transition-all"
                            placeholder="e.g. Danfo bus, Berger Park"
                            value={formData.location}
                            onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                          />
                        </div>
                      </>
                    ) : type === 'nuisance' ? (
                      <>
                        <div className="grid grid-cols-2 gap-3">
                          <div className="space-y-1">
                            <label className="text-xs font-bold text-zinc-500 uppercase ml-1">Issue Type</label>
                            <select
                              className="w-full bg-zinc-50 border border-zinc-200 rounded-xl py-3 px-3 text-sm focus:ring-2 focus:ring-green-600/10 focus:border-green-600 outline-hidden transition-all appearance-none"
                              value={formData.nuisanceType}
                              onChange={(e) => setFormData({ ...formData, nuisanceType: e.target.value as any })}
                            >
                              <option value="noise">Excessive Noise</option>
                              <option value="blockage">Illegal Road Blockage</option>
                              <option value="illegal">Illegal Dumping</option>
                              <option value="drainage">Blocked Drainage</option>
                            </select>
                          </div>
                          <div className="space-y-1">
                            <label className="text-xs font-bold text-zinc-500 uppercase ml-1">Severity</label>
                            <select
                              className="w-full bg-zinc-50 border border-zinc-200 rounded-xl py-3 px-3 text-sm focus:ring-2 focus:ring-green-600/10 focus:border-green-600 outline-hidden transition-all appearance-none"
                              value={formData.severity}
                              onChange={(e) => setFormData({ ...formData, severity: e.target.value as any })}
                            >
                              <option value="low">Low</option>
                              <option value="medium">Medium</option>
                              <option value="high">High (Immediate Action)</option>
                            </select>
                          </div>
                        </div>
                        <div className="space-y-1">
                          <label className="text-xs font-bold text-zinc-500 uppercase ml-1">Specific Location</label>
                          <input
                            required
                            type="text"
                            className="w-full bg-zinc-50 border border-zinc-200 rounded-xl py-3 px-4 text-sm focus:ring-2 focus:ring-green-600/10 focus:border-green-600 outline-hidden transition-all"
                            placeholder="e.g. St Finbarr\'s Rd"
                            value={formData.location}
                            onChange={(e) => setFormData({ ...formData, location: e.target.value, title: e.target.value })}
                          />
                        </div>
                      </>
                    ) : type === 'water' ? (
                      <>
                        <div className="space-y-1">
                          <label className="text-xs font-bold text-zinc-500 uppercase ml-1">Vendor Name / Tanker No.</label>
                          <input
                            required
                            type="text"
                            className="w-full bg-zinc-50 border border-zinc-200 rounded-xl py-3 px-4 text-sm focus:ring-2 focus:ring-green-600/10 focus:border-green-600 outline-hidden transition-all"
                            placeholder="e.g. Alhaji Sani Water"
                            value={formData.vendorName}
                            onChange={(e) => setFormData({ ...formData, vendorName: e.target.value, title: e.target.value })}
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div className="space-y-1">
                            <label className="text-xs font-bold text-zinc-500 uppercase ml-1">Status</label>
                            <select
                              className="w-full bg-zinc-50 border border-zinc-200 rounded-xl py-3 px-3 text-sm focus:ring-2 focus:ring-green-600/10 focus:border-green-600 outline-hidden transition-all appearance-none"
                              value={formData.status}
                              onChange={(e) => setFormData({ ...formData, status: e.target.value as any })}
                            >
                              <option value="available">Available Now</option>
                              <option value="busy">Busy / On-route</option>
                              <option value="unavailable">Not Active</option>
                            </select>
                          </div>
                          <div className="space-y-1">
                            <label className="text-xs font-bold text-zinc-500 uppercase ml-1">Price approx.</label>
                            <input
                              required
                              type="text"
                              className="w-full bg-zinc-50 border border-zinc-200 rounded-xl py-3 px-4 text-sm focus:ring-2 focus:ring-green-600/10 focus:border-green-600 outline-hidden transition-all"
                              placeholder="e.g. 15000"
                              value={formData.price}
                              onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                            />
                          </div>
                        </div>
                        <div className="space-y-1">
                          <label className="text-xs font-bold text-zinc-500 uppercase ml-1">Service Area</label>
                          <input
                            required
                            type="text"
                            className="w-full bg-zinc-50 border border-zinc-200 rounded-xl py-3 px-4 text-sm focus:ring-2 focus:ring-green-600/10 focus:border-green-600 outline-hidden transition-all"
                            placeholder="e.g. Gwarinpa Estate"
                            value={formData.location}
                            onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                          />
                        </div>
                      </>
                    ) : (
                      <div className="space-y-1">
                        <label className="text-xs font-bold text-zinc-500 uppercase ml-1">Location Details</label>
                        <div className="relative">
                          <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                          <input
                            required
                            type="text"
                            className="w-full bg-zinc-50 border border-zinc-200 rounded-xl py-3 pl-10 pr-4 text-sm focus:ring-2 focus:ring-green-600/10 focus:border-green-600 outline-hidden transition-all"
                            placeholder="Street or Landmark"
                            value={formData.location}
                            onChange={(e) => setFormData({ ...formData, location: e.target.value, title: e.target.value })}
                          />
                        </div>
                      </div>
                    )}

                    <div className="space-y-1">
                      <label className="text-xs font-bold text-zinc-500 uppercase ml-1">Photo (Optional)</label>
                      <button 
                        type="button"
                        className="w-full bg-zinc-50 border border-zinc-200 border-dashed rounded-xl py-4 flex flex-col items-center justify-center gap-1.5 hover:bg-zinc-100 transition-all group"
                        onClick={() => toast.info('Photo upload coming soon', { description: 'We are currently setting up secure media servers for Lagos & Abuja.' })}
                      >
                        <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center text-zinc-400 group-hover:text-green-600 transition-colors shadow-sm">
                          <Camera className="w-4 h-4" />
                        </div>
                        <span className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest">Add a photo of the scene</span>
                      </button>
                    </div>

                    <div className="space-y-1 relative">
                      <div className="flex justify-between items-center ml-1">
                        <label className="text-xs font-bold text-zinc-500 uppercase">Description</label>
                        <AnimatePresence>
                          {isScanning && (
                            <motion.div 
                              initial={{ opacity: 0, x: 10 }}
                              animate={{ opacity: 1, x: 0 }}
                              exit={{ opacity: 0, x: 10 }}
                              className="flex items-center gap-1.5"
                            >
                              <div className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse" />
                              <span className="text-[8px] font-black text-green-600 uppercase tracking-widest">Sentlo AI Shield Scanning...</span>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                      <textarea
                        required
                        rows={2}
                        className={`w-full bg-zinc-50 border rounded-xl py-3 px-4 text-sm focus:ring-2 focus:ring-green-600/10 focus:border-green-600 outline-hidden transition-all resize-none ${
                          safetyScore < 70 ? 'border-red-200' : 'border-zinc-200'
                        }`}
                        placeholder="Add more details..."
                        value={formData.description}
                        onChange={(e) => {
                          const val = e.target.value;
                          setFormData({ ...formData, description: val });
                          
                          // Trigger real-time filtering
                          setIsScanning(true);
                          const isRestricted = validateContent(val);
                          setSafetyScore(isRestricted ? 40 : 100);
                          
                          setTimeout(() => setIsScanning(false), 800);
                        }}
                      />
                      {safetyScore < 70 && (
                        <p className="text-[8px] font-bold text-red-500 uppercase tracking-widest mt-1 ml-1 animate-bounce">
                          Restricted keywords detected. Please follow community privacy rules.
                        </p>
                      )}
                    </div>

                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className={`w-full text-white py-4 rounded-xl font-bold flex items-center justify-center gap-2 transition-all shadow-xl ${
                        isSubmitting 
                          ? 'bg-zinc-400 cursor-not-allowed' 
                          : 'bg-zinc-900 hover:bg-zinc-800'
                      }`}
                    >
                      {isSubmitting ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          Posting...
                        </>
                      ) : (
                        <>
                          {promoTier !== 'none' ? 'Continue to Payment' : 'Post Update'}
                          <Send className="w-4 h-4" />
                        </>
                      )}
                    </button>
                  </form>
                </>
              ) : (
                <div className="space-y-6">
                  <div className="p-4 rounded-2xl border bg-blue-50 border-blue-100 flex items-center justify-between">
                    <div>
                      <p className="text-xs font-bold text-blue-900">Promotion Amount</p>
                      <p className="text-2xl font-black text-blue-600">₦{PRICING[promoTier].toLocaleString()}</p>
                    </div>
                  </div>
                  <button
                    onClick={processPayment}
                    disabled={isProcessing}
                    className="w-full bg-green-600 text-white py-4 rounded-xl font-bold flex items-center justify-center gap-2 hover:bg-green-700 transition-all shadow-xl"
                  >
                    {isProcessing ? 'Processing...' : 'Pay Now'}
                  </button>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}